/**
 * Speech-to-Text Service (Browser-based)
 * Uses Web Speech API for real-time speech recognition
 * 
 * Architecture: Zero-gap continuous recognition with transcript accumulator.
 * The Web Speech API fires `onend` frequently (on silence, network hiccups,
 * Chrome's internal limits). This service handles restarts seamlessly,
 * preserving an accumulated transcript buffer across restarts so no words
 * are ever lost.
 *
 * Key improvements:
 * - Proactive session recycling (every 5 min) to prevent Chrome's 10m hard timeout
 * - Fresh SpeechRecognition instance instantiation on recovery to prevent IPC stale states
 * - Health-check watchdog targeting zombie LISTENING/STOPPING/STARTING deadlocks
 * - Separate resume() from start() to preserve transcript context
 * - Fail-safe timeouts for abort() and stop()
 */

class STTService {
  constructor() {
    this.recognition = null;
    this.state = 'IDLE'; // IDLE, STARTING, LISTENING, STOPPING
    this._stateChangedTime = Date.now();
    this._sessionStartTime = 0;
    this.shouldListen = false;
    this.onResult = null;
    this.onError = null;
    this.onEnd = null;
    this.onStart = null;
    this.language = 'en-IN';

    // ── Accumulator state ──
    this._accumulatedFinals = '';    
    this._lastResultTime = 0;       
    this._silenceTimer = null;       
    this._watchdogInterval = null;
    this._restartCount = 0;          
    this._lastInterim = '';          
    this._networkErrorCount = 0;
    this._consecutiveNoSpeech = 0;

    this.initializeRecognition();
  }

  /**
   * Create or recreate the SpeechRecognition instance with event bindings
   */
  _createRecognitionInstance() {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

    if (!SpeechRecognition) {
      console.error('[STT] Speech Recognition API not supported in this browser');
      return null;
    }

    // Clean up existing instance if any
    if (this.recognition) {
      try {
        this.recognition.onstart = null;
        this.recognition.onresult = null;
        this.recognition.onerror = null;
        this.recognition.onend = null;
        this.recognition.onnomatch = null;
        this.recognition.abort();
      } catch (e) {
        // ignore errors during cleanup
      }
    }

    const instance = new SpeechRecognition();
    instance.continuous = true;
    instance.interimResults = true;
    instance.lang = this.language;
    instance.maxAlternatives = 1;

    // Event handlers
    instance.onstart = () => {
      this.state = 'LISTENING';
      this._stateChangedTime = Date.now();
      this._sessionStartTime = Date.now();
      this._consecutiveNoSpeech = 0;
      console.log('[STT] Listening started.');
      if (this.onStart) {
        this.onStart();
      }
    };

    instance.onresult = (event) => {
      this._lastResultTime = Date.now();
      this._resetSilenceTimer();
      this._networkErrorCount = 0; // Reset on successful result
      this._consecutiveNoSpeech = 0;

      let currentInterim = '';

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        const transcript = result[0].transcript;
        const confidence = result[0].confidence || 0.8;

        if (result.isFinal) {
          if (transcript.trim()) {
            if (this._accumulatedFinals && !this._accumulatedFinals.endsWith(' ')) {
              this._accumulatedFinals += ' ';
            }
            this._accumulatedFinals += transcript.trim();
          }

          if (this.onResult && transcript.trim()) {
            this.onResult({
              finalText: transcript.trim(),
              interimText: '',
              confidence: confidence,
              isFinal: true
            });
          }
        } else {
          currentInterim += transcript;
        }
      }

      if (currentInterim && this.onResult) {
        this._lastInterim = currentInterim;
        this.onResult({
          finalText: '',
          interimText: currentInterim,
          confidence: 0.5,
          isFinal: false
        });
      }
    };

    instance.onnomatch = () => {
      console.log('[STT] No match detected');
    };

    instance.onerror = (event) => {
      console.warn('[STT] Error:', event.error);

      if (event.error === 'network') {
        this._networkErrorCount++;
      }

      if (event.error === 'no-speech') {
        this._consecutiveNoSpeech++;
        if (this._consecutiveNoSpeech > 5) {
          console.log('[STT] Too many no-speech errors, backing off...');
        }
      }

      // Recoverable errors — onend will handle restart
      if (['no-speech', 'audio-capture', 'network', 'aborted'].includes(event.error)) {
        return;
      }

      // Non-recoverable: 'not-allowed', 'service-not-allowed', 'language-not-supported'
      this.state = 'IDLE';
      this._stateChangedTime = Date.now();
      if (this.onError) {
        this.onError(event.error);
      }
    };

    instance.onend = () => {
      const wasShouldListen = this.shouldListen;
      const wasStarting = this.state === 'STARTING';
      this.state = 'IDLE';
      this._stateChangedTime = Date.now();

      if (wasShouldListen) {
        this._restartCount++;
        this._lastInterim = ''; 
        let delay = wasStarting ? 400 : 100;
        if (this._networkErrorCount > 0) {
          delay = Math.min(1000 * Math.pow(2, this._networkErrorCount - 1), 5000);
          console.log(`[STT] Retrying in ${delay}ms due to network error...`);
        } else if (this._consecutiveNoSpeech > 3) {
          delay = Math.min(500 * (this._consecutiveNoSpeech - 3), 2000);
          console.log(`[STT] Retrying in ${delay}ms after ${this._consecutiveNoSpeech} no-speech errors...`);
        } else if (wasStarting) {
          console.log(`[STT] Retrying in ${delay}ms after premature end in STARTING state...`);
        }

        setTimeout(() => {
          if (this.shouldListen && this.state === 'IDLE') {
            const forceNew = this._pendingForceNewInstance || false;
            this._pendingForceNewInstance = false;
            this._internalStart(forceNew);
          }
        }, delay);
      } else {
        if (this.onEnd) {
          this.onEnd();
        }
      }
    };

    this.recognition = instance;
    return instance;
  }

  /**
   * Initialize Web Speech Recognition
   */
  initializeRecognition() {
    this._createRecognitionInstance();
  }

  _resetSilenceTimer() {
    if (this._silenceTimer) {
      clearTimeout(this._silenceTimer);
    }
    // 90s silence timer — triggers force restart if user hasn't spoken
    this._silenceTimer = setTimeout(() => {
      if (this.shouldListen && this.state === 'LISTENING') {
        console.log('[STT] Silence timeout reached, recycling connection...');
        this._forceRestart(true);
      }
    }, 90000);
  }

  _forceRestart(forceNewInstance = true) {
    if (!this.shouldListen) return;
    this._pendingForceNewInstance = forceNewInstance;
    try {
      this.state = 'STOPPING';
      this._stateChangedTime = Date.now();
      if (this.recognition) {
        this.recognition.abort();
      }
    } catch (e) {
      this.state = 'IDLE';
      this._stateChangedTime = Date.now();
    }

    // Safety fallback: if onend fails to fire within 800ms, force state reset and restart
    setTimeout(() => {
      if (this.shouldListen && (this.state === 'STOPPING' || this.state === 'STARTING')) {
        console.warn('[STT] Force restart safety fallback triggered.');
        this.state = 'IDLE';
        this._stateChangedTime = Date.now();
        const forceNew = this._pendingForceNewInstance;
        this._pendingForceNewInstance = false;
        this._internalStart(forceNew);
      }
    }, 800);
  }

  _internalStart(forceNewInstance = false) {
    if (!this.shouldListen) return;
    if (this.state !== 'IDLE') return;

    if (forceNewInstance || !this.recognition) {
      this._createRecognitionInstance();
    }

    if (!this.recognition) return;

    // Ensure language is up-to-date before each start
    this.recognition.lang = this.language;
    
    this.state = 'STARTING';
    this._stateChangedTime = Date.now();
    try {
      this.recognition.start();
    } catch (err) {
      console.warn('[STT] Internal start failed:', err.name || err);
      this.state = 'IDLE';
      this._stateChangedTime = Date.now();
      if (err.name === 'InvalidStateError' || err.name === 'NotAllowedError') {
        // Recreate instance if browser state is invalid
        this._createRecognitionInstance();
      }
    }
  }

  _ensureWatchdogRunning() {
    if (this._watchdogInterval) return;
    this._watchdogInterval = setInterval(() => {
      this._runHealthCheck();
    }, 5000);
  }

  _runHealthCheck() {
    if (!this.shouldListen) return;
    const now = Date.now();

    // 1. Recover from stuck STOPPING state (> 2s)
    if (this.state === 'STOPPING' && now - this._stateChangedTime > 2000) {
      console.warn('[STT Watchdog] Detected stuck STOPPING state — forcing reset and recreation.');
      this.state = 'IDLE';
      this._stateChangedTime = now;
      this._internalStart(true);
      return;
    }

    // 2. Recover from stuck STARTING state (> 4s)
    if (this.state === 'STARTING' && now - this._stateChangedTime > 4000) {
      console.warn('[STT Watchdog] Detected stuck STARTING state — forcing reset and recreation.');
      this.state = 'IDLE';
      this._stateChangedTime = now;
      this._internalStart(true);
      return;
    }

    // 3. Proactive session recycling (prevent Chrome 10-minute hard timeout)
    if (this.state === 'LISTENING' && this._sessionStartTime > 0 && now - this._sessionStartTime > 300000) {
      console.log('[STT Watchdog] Proactive 5-minute session recycling to prevent Chrome STT drop...');
      this._forceRestart(true);
      return;
    }

    // 4. Recover from dead LISTENING state (no results for > 90s despite shouldListen)
    if (this.state === 'LISTENING' && this._lastResultTime > 0 && now - this._lastResultTime > 90000) {
      console.warn('[STT Watchdog] Silent listening connection detected — recycling recognition engine.');
      this._forceRestart(true);
      return;
    }

    // 5. Recover from IDLE state
    if (this.state === 'IDLE') {
      console.warn('[STT Watchdog] Detected IDLE state while shouldListen is true — restarting...');
      this._internalStart(false);
      return;
    }
  }

  /**
   * Start a FRESH listening session.
   * Clears accumulated state. Use for the initial start of an interview.
   */
  start() {
    this.shouldListen = true;
    this._networkErrorCount = 0;
    this._restartCount = 0;
    this._accumulatedFinals = '';
    this._lastInterim = '';
    this._consecutiveNoSpeech = 0;
    this._lastResultTime = Date.now();

    if (this.state === 'LISTENING' || this.state === 'STARTING') {
      return;
    }

    if (this.state === 'STOPPING') {
      this.state = 'IDLE';
      this._stateChangedTime = Date.now();
    }

    this._internalStart(true);
    this._ensureWatchdogRunning();
  }

  /**
   * Resume listening WITHOUT clearing accumulated state.
   * Use this when re-enabling STT after AI finishes speaking.
   * This is the key difference from start() — it preserves context.
   */
  resume() {
    this.shouldListen = true;
    this._networkErrorCount = 0;
    this._consecutiveNoSpeech = 0;
    this._lastResultTime = Date.now();
    // NOTE: We do NOT clear _accumulatedFinals, _restartCount, or _lastInterim

    if (this.state === 'LISTENING' || this.state === 'STARTING') {
      return;
    }

    if (this.state === 'STOPPING') {
      this.state = 'IDLE';
      this._stateChangedTime = Date.now();
    }

    this._internalStart(false);
    this._ensureWatchdogRunning();
  }

  /**
   * Stop listening
   */
  stop() {
    this.shouldListen = false;
    if (this._silenceTimer) {
      clearTimeout(this._silenceTimer);
      this._silenceTimer = null;
    }
    if (this._watchdogInterval) {
      clearInterval(this._watchdogInterval);
      this._watchdogInterval = null;
    }

    if (this.state === 'LISTENING' || this.state === 'STARTING') {
      this.state = 'STOPPING';
      this._stateChangedTime = Date.now();
      try {
        if (this.recognition) {
          this.recognition.stop();
        }
      } catch (e) {
        this.state = 'IDLE';
        this._stateChangedTime = Date.now();
      }

      // Safety fallback to prevent permanently stuck STOPPING state
      setTimeout(() => {
        if (this.state === 'STOPPING') {
          this.state = 'IDLE';
          this._stateChangedTime = Date.now();
        }
      }, 1000);
    }
  }

  /**
   * Abort recognition
   */
  abort() {
    this.shouldListen = false;
    if (this._silenceTimer) {
      clearTimeout(this._silenceTimer);
      this._silenceTimer = null;
    }
    if (this._watchdogInterval) {
      clearInterval(this._watchdogInterval);
      this._watchdogInterval = null;
    }

    if (this.state !== 'IDLE') {
      this.state = 'STOPPING';
      this._stateChangedTime = Date.now();
      try {
        if (this.recognition) {
          this.recognition.abort();
        }
      } catch (e) {
        this.state = 'IDLE';
        this._stateChangedTime = Date.now();
      }

      setTimeout(() => {
        if (this.state === 'STOPPING') {
          this.state = 'IDLE';
          this._stateChangedTime = Date.now();
        }
      }, 1000);
    }
  }

  /**
   * Get the accumulated final transcript and clear it.
   */
  consumeAccumulatedFinals() {
    const text = this._accumulatedFinals.trim();
    this._accumulatedFinals = '';
    return text;
  }

  getAccumulatedFinals() {
    return this._accumulatedFinals.trim();
  }

  clearAccumulatedFinals() {
    this._accumulatedFinals = '';
  }

  setLanguage(lang) {
    this.language = lang;
    if (this.recognition) {
      this.recognition.lang = lang;
    }
  }

  setOnResult(callback) {
    this.onResult = callback;
  }

  setOnError(callback) {
    this.onError = callback;
  }

  setOnEnd(callback) {
    this.onEnd = callback;
  }

  setOnStart(callback) {
    this.onStart = callback;
  }

  getIsListening() {
    return this.state === 'LISTENING';
  }

  static isSupported() {
    return !!(window.SpeechRecognition || window.webkitSpeechRecognition);
  }
}

// Export STTService class and singleton instance
export { STTService };
export const sttService = new STTService();
export default sttService;

